import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import FieldPanel from '@/components/chat/FieldPanel';
import SummarizerSettingsBar, {
  type SummarizerSettings,
} from '@/components/tools/SummarizerSettingsBar';
import { summarizeNews } from '@/services/summarizerAgentService';
import { useAuth } from '@/contexts/AuthWrapper';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styles from '@/styles/pages/TextChatPage.module.css';

type AgentMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
};

const STORAGE_KEY = 'agent_chat_summarizer_v1';
const SETTINGS_KEY = 'agent_settings_summarizer_v1';

function generateId(prefix: string) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function safeParseJSON<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

const SummarizerAgentPage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState<{
    right?: number;
    bottom?: number;
  }>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [settings, setSettings] = useState<SummarizerSettings>(() => {
    const saved = safeParseJSON<SummarizerSettings>(localStorage.getItem(SETTINGS_KEY));
    return (
      saved || {
        lang: 'en',
        region: 'uk',
        timeRange: 'day',
      }
    );
  });

  const token = useMemo(() => {
    // Prefer the account token returned by /me (stored in AuthContext user)
    return user?.token || '';
  }, [user?.token]);

  // Load history
  useEffect(() => {
    const saved = safeParseJSON<
      Array<{ id: string; role: 'user' | 'assistant'; content: string; timestamp: string }>
    >(localStorage.getItem(STORAGE_KEY));

    if (saved && Array.isArray(saved)) {
      setMessages(
        saved.map(m => ({
          ...m,
          timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
        }))
      );
    }
  }, []);

  // Save history
  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(
        messages.map(m => ({
          ...m,
          timestamp: m.timestamp.toISOString(),
        }))
      )
    );
  }, [messages]);

  // Save settings
  useEffect(() => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  }, [settings]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = useCallback(async () => {
    const query = inputValue.trim();
    if (!query || isLoading) return;

    setNotice(null);
    setIsLoading(true);

    const userMsg: AgentMessage = {
      id: generateId('msg'),
      role: 'user',
      content: query,
      timestamp: new Date(),
    };
    const assistantMsgId = generateId('msg');
    const assistantMsg: AgentMessage = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setInputValue('');

    try {
      if (!token) {
        throw new Error(
          'Missing account token. Please re-login so we can use your account token for the summarizer request.'
        );
      }

      const res = await summarizeNews({
        lang: settings.lang,
        region: settings.region,
        query,
        time_range: settings.timeRange,
        token,
      });

      const text =
        (typeof (res as any)?.summary === 'string' && (res as any).summary) ||
        (typeof (res as any)?.result === 'string' && (res as any).result) ||
        JSON.stringify(res, null, 2);

      setMessages(prev =>
        prev.map(m => (m.id === assistantMsgId ? { ...m, content: text } : m))
      );
    } catch (e: any) {
      const code = (e as any)?.code;
      if (code === 'EMPTY_NEWS_OUTPUT') {
        setNotice(
          'No news results found for that query/settings. Try a broader query, switch Region/Language, or increase Time range (e.g. Week/Month/Year).'
        );
      } else {
        const message =
          e instanceof Error ? e.message : 'Error calling summarizer agent';
        setNotice(message);
      }
      setMessages(prev => prev.filter(m => m.id !== assistantMsgId));
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, settings.lang, settings.region, settings.timeRange, token]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // Ensure query params do not break anything (keeps parity with other pages)
  useEffect(() => {
    void location.search;
  }, [location.search]);

  return (
    <div className={styles.textChatPage}>
      <div className={styles.inputUnderlay} />

      <div className={styles.chatContainer}>
        <div className={styles.messagesArea}>
          {messages.map(message => (
            <div key={message.id} className={styles.messageGroup}>
              {message.role === 'user' ? (
                <div className={styles.userMessage}>
                  <div className={styles.messageContent}>{message.content}</div>
                </div>
              ) : (
                <div className={styles.aiMessage}>
                  <div className={styles.aiContent}>
                    {message.content ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {message.content}
                      </ReactMarkdown>
                    ) : isLoading ? (
                      'Summarizing…'
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {notice && (
            <div className={styles.messageGroup}>
              <div className={styles.aiMessage}>
                <div className={styles.errorBubble}>{notice}</div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className={styles.inputArea}>
        <FieldPanel
          value={inputValue}
          onChange={setInputValue}
          onSend={handleSend}
          onKeyDown={handleKeyDown}
          onToolsClick={(anchorRect?: DOMRect) => {
            if (anchorRect) {
              const viewportHeight = window.innerHeight;
              const bottom = Math.max(20, viewportHeight - anchorRect.top + 12);
              const right = Math.max(20, window.innerWidth - anchorRect.right);
              setSettingsAnchor({ bottom, right });
            } else {
              setSettingsAnchor({});
            }
            setIsSettingsOpen(true);
          }}
          disabled={isLoading}
        />
      </div>

      <SummarizerSettingsBar
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        anchorRight={settingsAnchor.right}
        anchorBottom={settingsAnchor.bottom}
        value={settings}
        onChange={setSettings}
      />
    </div>
  );
};

export default SummarizerAgentPage;

